import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock, Loader2, Eye, RefreshCw } from "lucide-react";
import ExportButton from "@/components/ExportButton";

const money = (v) => `$${(Number(v) || 0).toLocaleString("es-EC", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const uds = (v) => (Number(v) || 0).toLocaleString("es-EC", { maximumFractionDigits: 2 });
const mesActual = () => new Date().toISOString().slice(0, 7);

function DetalleCierre({ periodo, onClose }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    api.get(`/inventory-report/cierres/${periodo}`).then(r => setDoc(r.data)).catch(e => toast.error(formatApiError(e)));
  }, [periodo]);
  const cols = [{ label: "Código", accessor: "codigo_interno" }, { label: "Producto", accessor: "nombre_comercial" }, { label: "Laboratorio", accessor: "laboratorio" },
    { label: "Inicial uds", accessor: "inicial_uds" }, { label: "Inicial valor", accessor: "inicial_valor" }, { label: "Entradas valor", accessor: "entradas_valor" },
    { label: "Salidas valor", accessor: "salidas_valor" }, { label: "Final uds", accessor: "final_uds" }, { label: "Final valor", accessor: "final_valor" }];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-auto" data-testid="cierre-detalle-dialog">
        <DialogHeader><DialogTitle>Cierre de inventario {periodo}</DialogTitle></DialogHeader>
        {!doc ? <Loader2 className="animate-spin" /> : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span>Corte: <strong>{doc.fecha_corte}</strong></span>
              <span>Inicial: <strong>{money(doc.totales.inicial_valor)}</strong></span>
              <span>Entradas: <strong>{money(doc.totales.entradas_valor)}</strong></span>
              <span>Salidas: <strong>−{money(doc.totales.salidas_valor)}</strong></span>
              <span className="text-[#205c45]">Final: <strong data-testid="cierre-detalle-final">{money(doc.totales.final_valor)}</strong></span>
              <div className="flex-1" />
              <ExportButton title={`Cierre de inventario ${periodo} (corte ${doc.fecha_corte})`} columns={cols} rows={doc.rows} filename={`cierre-inventario-${periodo}`} />
            </div>
            {doc.por_bodega?.length > 0 && <div className="text-xs text-[#4a5c53]">{doc.por_bodega.map(b => `${b.bodega}: ${money(b.valor)}`).join(" · ")}</div>}
            {doc.historial?.length > 0 && <div className="text-xs text-[#8a5a00]" data-testid="cierre-historial">{doc.historial.map((h, i) => <div key={i}>Recalculado {h.at?.slice(0, 16).replace("T", " ")} por {h.por}: antes {money(h.valor_anterior)} ({h.motivo})</div>)}</div>}
            <table className="erp-table">
              <thead><tr><th>Producto</th><th className="text-right">Inicial</th><th className="text-right">Entradas</th><th className="text-right">Salidas</th><th className="text-right">Final</th></tr></thead>
              <tbody>{doc.rows.map(r => (
                <tr key={r.producto_comercial_id}>
                  <td className="text-xs">{r.nombre_comercial}<div className="text-[10px] text-[#8ba397]">{r.laboratorio || ""}</div></td>
                  <td className="text-right tabular-nums text-xs">{money(r.inicial_valor)}</td>
                  <td className="text-right tabular-nums text-xs">{money(r.entradas_valor)}</td>
                  <td className="text-right tabular-nums text-xs">{money(r.salidas_valor)}</td>
                  <td className="text-right tabular-nums text-xs font-semibold">{money(r.final_valor)}<div className="text-[10px] text-[#5c7368] font-normal">{uds(r.final_uds)} uds</div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CierresInventario() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [periodo, setPeriodo] = useState(mesActual());
  const [saving, setSaving] = useState(false);
  const [ver, setVer] = useState(null);
  const puedeCerrar = ["admin", "contabilidad", "gerencia"].includes(user?.role);
  const load = useCallback(() => api.get("/inventory-report/cierres").then(r => setItems(r.data)).catch(e => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const cerrar = async (p, recalcular = false) => {
    if (recalcular && !window.confirm(`¿Recalcular el cierre de ${p}? El valor anterior queda en el historial.`)) return;
    setSaving(true);
    try {
      const { data } = await api.post("/inventory-report/cierres", { periodo: p, motivo: recalcular ? "Recalculado por administrador" : undefined });
      toast.success(`Mes ${data.periodo} cerrado: ${money(data.totales.final_valor)}${data.parcial ? " (parcial, al día de hoy)" : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-md border border-[#d1dbd6] bg-white p-4 space-y-3" data-testid="cierres-inventario">
      <div className="flex flex-wrap items-end gap-3">
        <div className="mr-auto">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[#5c7368]"><Lock size={14} />Cierres mensuales de inventario</div>
          <p className="text-xs text-[#4a5c53] mt-1 max-w-xl">Se cierra solo al terminar cada mes (valor al último día, costo de cada lote) y queda fijo. Un cierre manual del mes en curso es parcial y lo reemplaza el automático. Solo un administrador puede recalcular un mes cerrado.</p>
        </div>
        {puedeCerrar && (
          <>
            <div><Label className="text-[11px]">Mes</Label><Input type="month" value={periodo} max={mesActual()} onChange={(e) => setPeriodo(e.target.value)} data-testid="cierre-periodo" /></div>
            <Button className="bg-[#205c45]" onClick={() => cerrar(periodo)} disabled={saving || !periodo} data-testid="cierre-cerrar-btn">
              {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Lock size={14} className="mr-1" />}Cerrar mes
            </Button>
          </>
        )}
      </div>
      <table className="erp-table">
        <thead><tr><th>Mes</th><th>Corte</th><th className="text-right">Inicial</th><th className="text-right">Entradas</th><th className="text-right">Salidas</th><th className="text-right">Valor final</th><th>Tipo</th><th>Cerrado por</th><th></th></tr></thead>
        <tbody>
          {items.map(c => (
            <tr key={c.id} data-testid={`cierre-row-${c.periodo}`}>
              <td className="font-mono text-xs font-semibold">{c.periodo}</td>
              <td className="text-xs">{c.fecha_corte}</td>
              <td className="text-right tabular-nums text-xs">{money(c.totales?.inicial_valor)}</td>
              <td className="text-right tabular-nums text-xs">{money(c.totales?.entradas_valor)}</td>
              <td className="text-right tabular-nums text-xs">−{money(c.totales?.salidas_valor)}</td>
              <td className="text-right tabular-nums font-semibold">{money(c.totales?.final_valor)}</td>
              <td><Badge variant="outline" className="text-[10px]">{c.tipo}{c.parcial ? " · parcial" : ""}</Badge></td>
              <td className="text-xs">{c.cerrado_por_nombre}<div className="text-[10px] text-[#8ba397]">{c.created_at?.slice(0, 16).replace("T", " ")}</div></td>
              <td className="text-right whitespace-nowrap">
                <Button size="sm" variant="outline" onClick={() => setVer(c.periodo)} data-testid={`cierre-ver-${c.periodo}`}><Eye size={13} className="mr-1" />Ver</Button>
                {user?.role === "admin" && <Button size="sm" variant="ghost" onClick={() => cerrar(c.periodo, true)} disabled={saving} title="Recalcular (admin)" data-testid={`cierre-recalcular-${c.periodo}`}><RefreshCw size={13} /></Button>}
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={9} className="text-center py-6 text-xs text-[#8ba397]">Aún no hay meses cerrados</td></tr>}
        </tbody>
      </table>
      {ver && <DetalleCierre periodo={ver} onClose={() => setVer(null)} />}
    </div>
  );
}

export default function CierresInventarioPage() {
  return (
    <div className="space-y-6" data-testid="cierres-inventario-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Contabilidad</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Cierres de inventario</h1>
      </div>
      <CierresInventario />
    </div>
  );
}
